import type { CSSProperties } from 'react';
import type { CutWindow, SceneLayoutBlock } from '@/lib/transitions/buildSceneLayout';
import { outputFrameToSourceFrame } from '@/lib/transitions/mapTimes';
import { resolveTransitionStyles } from '@/lib/transitions/resolveTransitionStyles';
import { makeSceneVolume } from '@/lib/transitions/resolveSceneVolume';

export interface EditorPlaybackLayer {
  sourceTime: number;
  style: CSSProperties | null;
  volume: number;
}

function sceneSourceTime(block: SceneLayoutBlock, frame: number, fps: number): number {
  return (block.trimBefore + (frame - block.from)) / fps;
}

function styleForScene(cuts: CutWindow[], sceneIndex: number, frame: number): CSSProperties | null {
  let style: CSSProperties | null = null;
  for (const cut of cuts) {
    if (cut.index !== sceneIndex && cut.index !== sceneIndex - 1) continue;
    const resolved = resolveTransitionStyles(cut, frame);
    if (!resolved) continue;
    if (cut.index === sceneIndex && resolved.outgoing) style = { ...(style ?? {}), ...resolved.outgoing };
    if (cut.index === sceneIndex - 1 && resolved.incoming) style = { ...(style ?? {}), ...resolved.incoming };
  }
  return style;
}

function ownerIndex(blocks: SceneLayoutBlock[], frame: number): number {
  for (let i = 0; i < blocks.length; i++) {
    const block = blocks[i];
    if (frame >= block.from && frame < block.from + block.durationInFrames) return i;
  }
  return Math.max(0, blocks.length - 1);
}

/** One playing element for the whole clip. A second layer exists only while
 *  an overlap transition needs both scenes on screen. The primary element
 *  stays on the outgoing scene through that window so its clock is not
 *  yanked to the incoming shot at the midpoint. */
export function resolveEditorPlaybackFrame({
  frame,
  blocks,
  cuts,
  fps,
  baseVolume,
}: {
  frame: number;
  blocks: SceneLayoutBlock[];
  cuts: CutWindow[];
  fps: number;
  baseVolume: number;
}): { primary: EditorPlaybackLayer; incoming: EditorPlaybackLayer | null } {
  if (blocks.length === 0 || fps <= 0) {
    return { primary: { sourceTime: 0, style: null, volume: baseVolume }, incoming: null };
  }

  const overlap = cuts.find((cut) => (
    cut.overlapFrames > 0
    && frame >= cut.startFrame
    && frame < cut.endFrame
    && blocks[cut.index]
    && blocks[cut.index + 1]
  ));

  if (overlap) {
    const outgoing = blocks[overlap.index];
    const incoming = blocks[overlap.index + 1];
    const outgoingVolume = makeSceneVolume({
      baseVolume,
      fadeInFrames: 0,
      fadeOutFrames: overlap.overlapFrames,
      durationInFrames: outgoing.durationInFrames,
    })(frame - outgoing.from);
    const incomingVolume = makeSceneVolume({
      baseVolume,
      fadeInFrames: overlap.overlapFrames,
      fadeOutFrames: 0,
      durationInFrames: incoming.durationInFrames,
    })(frame - incoming.from);
    return {
      primary: {
        sourceTime: sceneSourceTime(outgoing, frame, fps),
        style: styleForScene(cuts, overlap.index, frame),
        volume: outgoingVolume,
      },
      incoming: {
        sourceTime: sceneSourceTime(incoming, frame, fps),
        style: styleForScene(cuts, overlap.index + 1, frame),
        volume: incomingVolume,
      },
    };
  }

  const index = ownerIndex(blocks, frame);
  return {
    primary: {
      sourceTime: outputFrameToSourceFrame(blocks, frame) / fps,
      style: styleForScene(cuts, index, frame),
      volume: baseVolume,
    },
    incoming: null,
  };
}
